import { useEffect, useMemo, useState } from 'react';

import { mapValues } from 'lodash';
import { useSnackbar } from 'notistack';

import type { OpportunityRoom as SalesforceRoom } from '~/hooks/apis/offers/useGetOffer';
import { useGetOfferPackages } from '~/hooks/apis/offers/useGetOfferPackages';
import { usePackageRatePlanSuggestions } from '~/hooks/apis/offers/usePackageRatePlanSuggestions';

import type { AppliedFlashRoomRate, SubmissionAttemptFailure } from '../types';

import { previewPackageLinks } from './linkPackages';
import { getRatePlanOptionsByRoomKey, resolvePackageRatePlans } from './ratePlanMapping';
import { useRoomMapping } from './useRoomMapping';
import { getMaxExtraNights, getOfferPackageName, getPackageSalesforceId, sortPackagesByLengthOfStay } from './utils';
import type { UnmatchedFlashPackage } from './utils';

export interface UsePackageSetupStepParams {
  appliedFlashRoomRates: Array<AppliedFlashRoomRate>;
  assetIdByPackageId: Record<string, string>;
  isPackageSetupStep: boolean;
  propertyId: string | undefined;
  /** A draft room's key -> the real id it has once created, so the mapping follows the room. */
  roomKeyAliases: Record<string, string>;
  roomTypes: Array<Pick<App.RoomType, 'id' | 'name'>>;
  salesforceRooms: Array<SalesforceRoom>;
  setIsAdvancingStep: (isAdvancing: boolean) => void;
  suggestedAssetRoomKeys: Record<string, string>;
  trimmedSalesforceOfferId: string;
}

/**
 * Owns the Package Setup step (8): matching applied room rates to Salesforce packages (a
 * preview against draft data -- real assignment happens at Submission), and the flexible-nights
 * toggle a CM sets per package, held as a draft until Submission applies it for real. The room
 * mapping it links against lives in `useRoomMapping`.
 */
export function usePackageSetupStep({
  appliedFlashRoomRates,
  assetIdByPackageId,
  isPackageSetupStep,
  propertyId,
  roomKeyAliases,
  roomTypes,
  salesforceRooms,
  setIsAdvancingStep,
  suggestedAssetRoomKeys,
  trimmedSalesforceOfferId,
}: UsePackageSetupStepParams) {
  const { enqueueSnackbar } = useSnackbar();
  const [matchedPackages, setMatchedPackages] = useState<Array<App.Package>>([]);
  const [isLinkingPackages, setIsLinkingPackages] = useState(false);
  const [hasAttemptedPackageLink, setHasAttemptedPackageLink] = useState(false);
  const [matchedRoomRateCount, setMatchedRoomRateCount] = useState(0);
  const [packageLinkFailures, setPackageLinkFailures] = useState<Array<string>>([]);
  const [unmatchedPackages, setUnmatchedPackages] = useState<Array<UnmatchedFlashPackage>>([]);
  const [assignableRoomTypes, setAssignableRoomTypes] = useState<Array<Pick<App.RoomType, 'id' | 'name'>>>([]);
  const [hasUnlinkedRoomChange, setHasUnlinkedRoomChange] = useState(false);
  const [flexibleNightsById, setFlexibleNightsById] = useState<Record<string, boolean>>({});
  const [ratePlanOverrides, setRatePlanOverrides] = useState<Record<string, string>>({});
  const [confirmedFlexibleNightsCount, setConfirmedFlexibleNightsCount] = useState(0);

  const roomMapping = useRoomMapping({
    assetIdByPackageId,
    roomKeyAliases,
    roomTypes,
    salesforceRooms,
    suggestedAssetRoomKeys,
  });
  const { packageRoomTypeIds } = roomMapping;
  const packageRoomTypeIdsKey = JSON.stringify(packageRoomTypeIds);
  // A package's room rate is its room plus its rate plan, so a room with several rate plans needs a pick per package.
  const ratePlanOptionsByRoomKey = getRatePlanOptionsByRoomKey(appliedFlashRoomRates);
  const { data: offerPackages = [], isFetching: isLoadingOfferPackages } = useGetOfferPackages(
    trimmedSalesforceOfferId,
    { enabled: isPackageSetupStep },
  );
  const ratePlanMatchRequests = offerPackages.flatMap((pkg) => {
    const packageId = getPackageSalesforceId(pkg);
    const roomKey = packageId ? packageRoomTypeIds[packageId] : undefined;
    const options = (roomKey && ratePlanOptionsByRoomKey[roomKey]) || [];

    return packageId && options.length > 1
      ? [
          {
            packageId,
            packageName: getOfferPackageName(pkg),
            ratePlanNames: options.map((option) => option.name),
            roomName: roomTypes.find((roomType) => roomType.id === roomKey)?.name,
          },
        ]
      : [];
  });
  const { hasSuggestionFailed, isSuggesting, suggestedRatePlanNameByPackageId } = usePackageRatePlanSuggestions(
    ratePlanMatchRequests,
    { enabled: isPackageSetupStep },
  );
  const suggestedRatePlanIds = Object.fromEntries(
    Object.entries(suggestedRatePlanNameByPackageId).flatMap(([packageId, ratePlanName]) => {
      const roomKey = packageRoomTypeIds[packageId];
      const option = ratePlanOptionsByRoomKey[roomKey]?.find((candidate) => candidate.name.trim() === ratePlanName);

      return option ? [[packageId, option.id]] : [];
    }),
  );
  const resolvedPackageRatePlans = resolvePackageRatePlans({
    overrides: ratePlanOverrides,
    packageRoomKeys: packageRoomTypeIds,
    ratePlanOptionsByRoomKey,
    suggestions: suggestedRatePlanIds,
  });
  const packageRatePlanIds = mapValues(resolvedPackageRatePlans, ({ ratePlanId }) => ratePlanId);
  const packageRatePlanSources = mapValues(resolvedPackageRatePlans, ({ source }) => source);
  const packageRatePlanIdsKey = JSON.stringify(packageRatePlanIds);
  // Derived from the current picks, not from the last link check.
  const hasUnresolvedRatePlanChoice = ratePlanMatchRequests.some(({ packageId }) => !packageRatePlanIds[packageId]);
  // Linking and the Next button wait for the package names and the AI's matches, so they run against the final picks.
  const isPreparingRatePlans = isSuggesting || (isPackageSetupStep && isLoadingOfferPackages);
  const sortedMatchedPackages = useMemo(() => sortPackagesByLengthOfStay(matchedPackages), [matchedPackages]);

  // The preview only reflects the mapping it ran with, so an edit from another step relinks on return;
  // picks made here keep the explicit "Retry linking" flow.
  useEffect(() => {
    if (!isPackageSetupStep && hasAttemptedPackageLink) {
      setHasAttemptedPackageLink(false);
      setPackageLinkFailures([]);
      setMatchedRoomRateCount(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only a changed mapping should reset the link attempt
  }, [packageRoomTypeIdsKey, packageRatePlanIdsKey]);

  // Newly matched packages get their Salesforce flexible_nights value seeded once; a package the
  // CM has already toggled here (still matched after a re-link) keeps that edit.
  useEffect(() => {
    setFlexibleNightsById((prev) => {
      let hasNewEntry = false;
      const next = { ...prev };

      matchedPackages.forEach((pkg) => {
        const packageId = getPackageSalesforceId(pkg);

        if (packageId && !(packageId in next)) {
          next[packageId] = 'flexible_nights' in pkg && !!pkg.flexible_nights;
          hasNewEntry = true;
        }
      });

      return hasNewEntry ? next : prev;
    });
  }, [matchedPackages]);

  const flexibleNightsWithoutMaxNights = matchedPackages.flatMap((pkg) => {
    const packageId = getPackageSalesforceId(pkg);
    const isFlexibleNights = (packageId && flexibleNightsById[packageId]) ?? false;
    const hasNoMaxNights = getMaxExtraNights(pkg) === 0;
    return packageId && isFlexibleNights && hasNoMaxNights ? [{ packageId, name: getOfferPackageName(pkg) }] : [];
  });
  const flexibleNightsNextStepsCount = flexibleNightsWithoutMaxNights.length || confirmedFlexibleNightsCount;
  const hasUnresolvedFlexibleNightsWarning = flexibleNightsWithoutMaxNights.length > 0;

  const isPackageSetupStepNextDisabled =
    isPackageSetupStep &&
    (!hasAttemptedPackageLink ||
      isLinkingPackages ||
      isPreparingRatePlans ||
      hasUnresolvedRatePlanChoice ||
      packageLinkFailures.length > 0 ||
      hasUnlinkedRoomChange ||
      hasUnresolvedFlexibleNightsWarning);

  async function assignRoomRatesToPackages(): Promise<{
    failures: Array<string>;
    matchedPackages: Array<App.Package>;
  }> {
    if (!propertyId || appliedFlashRoomRates.length === 0) {
      return { failures: [], matchedPackages: [] };
    }

    setIsAdvancingStep(true);

    const {
      assignableRoomTypes: linkedAssignableRoomTypes,
      isFatalError,
      matchedPackages: matched,
      matchedRoomRateCount: linkedCount,
      unmatchedPackageNames,
      unmatchedPackages: linkedUnmatchedPackages,
    } = await previewPackageLinks({
      appliedFlashRoomRates,
      packageRoomTypeIds,
      propertyId,
      ratePlanIdByPackageId: packageRatePlanIds,
      roomTypes,
      salesforceRooms,
      trimmedSalesforceOfferId,
    });

    setIsAdvancingStep(false);
    setAssignableRoomTypes(linkedAssignableRoomTypes);
    setUnmatchedPackages(linkedUnmatchedPackages);
    setMatchedRoomRateCount(linkedCount);
    setPackageLinkFailures(unmatchedPackageNames);

    if (!isFatalError && unmatchedPackageNames.length > 0) {
      enqueueSnackbar(
        `Unable to match ${unmatchedPackageNames.length} package${unmatchedPackageNames.length === 1 ? '' : 's'} to a room and rate`,
        { variant: 'warning' },
      );
    }

    return { failures: unmatchedPackageNames, matchedPackages: matched };
  }

  async function handleLinkPackages() {
    setHasAttemptedPackageLink(true);
    setHasUnlinkedRoomChange(false);
    setIsLinkingPackages(true);

    const { failures, matchedPackages: matched } = await assignRoomRatesToPackages();

    setIsLinkingPackages(false);
    setMatchedPackages(matched);

    return failures;
  }

  // Same readiness check as assignRoomRatesToPackages, so it can't fire early and get stuck
  // permanently no-op'd (hasAttemptedPackageLink locked true with no retry). Includes
  // salesforceRooms since buildFlashPackageRoomRateAssignments needs it to name unmatched packages.
  useEffect(() => {
    if (
      !isPackageSetupStep ||
      hasAttemptedPackageLink ||
      isLinkingPackages ||
      isPreparingRatePlans ||
      !propertyId ||
      appliedFlashRoomRates.length === 0 ||
      salesforceRooms.length === 0
    ) {
      return;
    }

    void handleLinkPackages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isPackageSetupStep,
    hasAttemptedPackageLink,
    isLinkingPackages,
    isPreparingRatePlans,
    propertyId,
    appliedFlashRoomRates.length,
    salesforceRooms.length,
  ]);

  // The preview no longer reflects the pick, so Next stays off until the links are re-checked.
  function selectRoomTypeForPackage(packageId: string, roomKey: string) {
    roomMapping.selectPackageRoom(packageId, roomKey);
    setHasUnlinkedRoomChange(true);
  }

  function selectRatePlanForPackage(packageId: string, ratePlanId: string) {
    setRatePlanOverrides((prev) => ({ ...prev, [packageId]: ratePlanId }));
    setHasUnlinkedRoomChange(true);
  }

  function toggleFlexibleNights(packageId: string, checked: boolean) {
    setFlexibleNightsById((prev) => ({ ...prev, [packageId]: checked }));
  }

  // Locally resolves an unmatchable flexible-nights package (no Salesforce max-nights value) by
  // turning its toggle off -- applying this for real is the Submission step's job.
  function confirmDisableFlexibleNights() {
    const targets = flexibleNightsWithoutMaxNights;

    if (targets.length === 0) {
      return;
    }

    setFlexibleNightsById((prev) => {
      const next = { ...prev };
      targets.forEach(({ packageId }) => {
        next[packageId] = false;
      });
      return next;
    });
    setConfirmedFlexibleNightsCount(targets.length);
  }

  // Only a package with no usable room mapping (pick a room, then "Retry linking") and a
  // max-nights-less flexible package ("Confirm" disables it) are fixable here -- any other failure is a backend rejection.
  function isSubmissionFailureActionable({
    flexibleNightsBlockedCount,
    packageUnmatchedCount,
  }: SubmissionAttemptFailure) {
    return packageUnmatchedCount > 0 || flexibleNightsBlockedCount > 0;
  }

  // A submission attempt can change the package data this step matched against, so clearing the
  // attempted flag lets the auto-link effect re-run and refresh the match on re-entry.
  function resetAfterFailedSubmission() {
    setHasAttemptedPackageLink(false);
    setPackageLinkFailures([]);
    setMatchedRoomRateCount(0);
  }

  function reset() {
    setMatchedPackages([]);
    setIsLinkingPackages(false);
    setHasAttemptedPackageLink(false);
    setMatchedRoomRateCount(0);
    setPackageLinkFailures([]);
    setUnmatchedPackages([]);
    setAssignableRoomTypes([]);
    roomMapping.reset();
    setHasUnlinkedRoomChange(false);
    setFlexibleNightsById({});
    setRatePlanOverrides({});
    setConfirmedFlexibleNightsCount(0);
  }

  return {
    assetRoomKeys: roomMapping.assetRoomKeys,
    assetRoomSources: roomMapping.assetRoomSources,
    assignableRoomTypes,
    confirmDisableFlexibleNights,
    flexibleNightsById,
    flexibleNightsNextStepsCount,
    flexibleNightsWithoutMaxNights,
    handleLinkPackages,
    resetAfterFailedSubmission,
    hasAttemptedPackageLink,
    hasRatePlanSuggestionFailed: hasSuggestionFailed,
    hasUnlinkedRoomChange,
    hasUnresolvedFlexibleNightsWarning,
    isLinkingPackages,
    isPackageSetupStepNextDisabled,
    isPreparingRatePlans,
    isSubmissionFailureActionable,
    matchedPackages,
    matchedRoomRateCount,
    packageLinkFailures,
    packageRatePlanIds,
    packageRatePlanSources,
    packageRoomSources: roomMapping.packageRoomSources,
    packageRoomTypeIds,
    ratePlanOptionsByRoomKey,
    reset,
    roomAssetNameByPackageId: roomMapping.roomAssetNameByPackageId,
    selectAssetRoom: roomMapping.selectAssetRoom,
    selectRatePlanForPackage,
    selectRoomTypeForPackage,
    sortedMatchedPackages,
    suggestedRatePlanIds,
    toggleFlexibleNights,
    unmatchedPackages,
  };
}
